import { useCallback, useEffect, useState } from 'react'

import type { CardPayload } from '@ankor-io/common/types/cards'

// Singleton class for card state management
// Will be creating separate PR to refactor overall fetching using react query patterns
class CardStore {
  private static instance: CardStore
  private cards: Map<string, CardPayload> = new Map()
  private cardsList: CardPayload[] = []
  private isLoading: boolean = false
  private listeners: Set<() => void> = new Set()

  private constructor() {}

  static getInstance(): CardStore {
    if (!CardStore.instance) {
      CardStore.instance = new CardStore()
    }
    return CardStore.instance
  }

  subscribe(listener: () => void): () => void {
    this.listeners.add(listener)
    return () => this.listeners.delete(listener)
  }

  getSnapshot(): { card: CardPayload | null; cards: CardPayload[]; isLoading: boolean } {
    return {
      card: this.cards.size > 0 ? Array.from(this.cards.values())[0] : null,
      cards: [...this.cardsList],
      isLoading: this.isLoading,
    }
  }

  getCard(id: string): CardPayload | null {
    return this.cards.get(id) || null
  }

  setCard(card: CardPayload): void {
    this.cards.set(card.id, card)
    // Update the list if this card is in it
    const index = this.cardsList.findIndex((c) => c.id === card.id)
    if (index !== -1) {
      this.cardsList[index] = card
    } else {
      this.cardsList.push(card)
    }
    this.notifyListeners()
  }

  setCards(cards: CardPayload[]): void {
    // Clear the map first, then update both the map and the list
    this.cards.clear()
    cards.forEach((card) => this.cards.set(card.id, card))
    this.cardsList = [...cards]
    this.notifyListeners()
  }

  setLoading(loading: boolean): void {
    this.isLoading = loading
    this.notifyListeners()
  }

  hasCards(): boolean {
    return this.cardsList.length > 0
  }

  removeCard(id: string): void {
    this.cards.delete(id)
    this.cardsList = this.cardsList.filter((card) => card.id !== id)
    this.notifyListeners()
  }

  clear(): void {
    this.cards.clear()
    this.cardsList = []
    this.notifyListeners()
  }

  hasCard(id: string): boolean {
    return this.cards.has(id)
  }

  private notifyListeners(): void {
    this.listeners.forEach((listener) => listener())
  }
}

// Get the singleton instance
const cardStore = CardStore.getInstance()

export const useCard = () => {
  const [snapshot, setSnapshot] = useState(() => cardStore.getSnapshot())

  useEffect(() => {
    return cardStore.subscribe(() => {
      setSnapshot(cardStore.getSnapshot())
    })
  }, [])

  const { card, cards, isLoading } = snapshot

  const setCard = useCallback((cardData: CardPayload) => {
    cardStore.setCard(cardData)
  }, [])

  const getCard = useCallback((id: string) => {
    return cardStore.getCard(id)
  }, [])

  const setCards = useCallback((cardsData: CardPayload[]) => {
    cardStore.setCards(cardsData)
  }, [])

  const setLoading = useCallback((loading: boolean) => {
    cardStore.setLoading(loading)
  }, [])

  const hasCards = useCallback(() => {
    return cardStore.hasCards()
  }, [])

  const removeCard = useCallback((id: string) => {
    cardStore.removeCard(id)
  }, [])

  const clearCards = useCallback(() => {
    cardStore.clear()
  }, [])

  const hasCard = useCallback((id: string) => {
    return cardStore.hasCard(id)
  }, [])

  return {
    card,
    cards,
    isLoading,
    setCard,
    getCard,
    setCards,
    setLoading,
    hasCards,
    removeCard,
    clearCards,
    hasCard,
  }
}
